import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useStore } from '../lib/useStore.jsx';
import { appOf, today } from '../lib/store.js';
import { SKILLS, BRANCHES } from '../content/skills.js';
import { progressOf, suggestedForAge, STATE_LABELS } from '../domain/skills.js';
import { ageAt } from '../domain/age.js';
import { BackIcon } from '../ui/Icons.jsx';

export default function ChooseSkills() {
  const { id } = useParams();
  const { store, parentUnlocked, toggleEnabled, enableMany, toggleAdultEnabled, setPrivilege } = useStore();
  const [showAll, setShowAll] = useState(false);
  const profile = store.profiles.find((p) => p.id === id);
  if (!parentUnlocked) return <Navigate to="/parent" replace />;
  if (!profile) return <Navigate to="/parent" replace />;
  const app = appOf(profile);
  const age = ageAt(today(), profile);
  const suggested = new Set(suggestedForAge(SKILLS, age));
  const enabled = new Set(app.enabled);
  const adult = new Set(app.adultEnabled);

  // "Usual at N" shows the age fit plus anything already ticked, so a parent's
  // own choices never vanish behind the filter. Suggested skills sort first.
  const visible = (list) => (showAll ? list : list.filter((s) => suggested.has(s.id) || enabled.has(s.id)));
  const sorted = (list) => [...list].sort((a, b) => Number(suggested.has(b.id)) - Number(suggested.has(a.id)));
  const missing = SKILLS.filter((s) => suggested.has(s.id) && !enabled.has(s.id) && s.safety !== 'adult_enables');

  return (
    <div className="stack">
      <Link to="/parent" className="btn quiet small" style={{ justifySelf: 'start' }}><BackIcon width="20" height="20" /> Family</Link>
      <h1>{profile.name}'s skills</h1>
      <p className="muted">Tick what goes on the wall. Skills marked <b>grown-up turns on</b> stay hidden until you switch them on for {profile.name}. A privilege is what independence unlocks: write it in their words.</p>

      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button type="button" className={`btn small ${showAll ? '' : 'primary'}`} onClick={() => setShowAll(false)}>Usual at {age}</button>
        <button type="button" className={`btn small ${showAll ? 'primary' : ''}`} onClick={() => setShowAll(true)}>Everything</button>
        <span style={{ flex: 1 }} />
        {missing.length > 0 && (
          <button type="button" className="btn small accent" onClick={() => enableMany(profile.id, missing.map((s) => s.id))}>
            Tick all usual at {age} ({missing.length})
          </button>
        )}
      </div>

      {BRANCHES.map((b) => {
        const rows = sorted(visible(SKILLS.filter((s) => s.branch === b.id)));
        if (rows.length === 0) return null;
        return (
          <section key={b.id} className="branch">
            <div className="branch-head"><span className="swatch" style={{ background: `var(--${b.color})` }} /><h2>{b.name}</h2></div>
            {rows.map((s) => {
              const on = enabled.has(s.id);
              const p = progressOf(app.events, s.id);
              return (
                <div key={s.id} className="check-row">
                  <input type="checkbox" id={`en-${s.id}`} checked={on} onChange={(e) => toggleEnabled(profile.id, s.id, e.target.checked)} />
                  <label htmlFor={`en-${s.id}`}>
                    <div style={{ fontWeight: 700 }}>{s.title} {suggested.has(s.id) && <span className="muted small">· usual at {age}</span>}</div>
                    <div className="small muted">{s.typicalAge[0]}–{s.typicalAge[1]} · {p.started ? STATE_LABELS[p.state] : 'not started'}{s.safety === 'with_adult' ? ' · with a grown-up' : ''}</div>
                  </label>
                  {s.safety === 'adult_enables' ? (
                    <label className="small row" style={{ gap: 6 }}>
                      <input type="checkbox" checked={adult.has(s.id)} onChange={(e) => toggleAdultEnabled(profile.id, s.id, e.target.checked)} /> grown-up turns on
                    </label>
                  ) : <span />}
                  {on && (
                    <input className="input privilege-input" placeholder="When independent, they can… (optional)" defaultValue={app.privileges[s.id] ?? ''}
                      onBlur={(e) => setPrivilege(profile.id, s.id, e.target.value)} />
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
      {!showAll && <p className="small muted">Showing the skills a {age}-year-old usually has a go at, plus anything already ticked. <button type="button" className="btn quiet small" onClick={() => setShowAll(true)}>Show everything</button></p>}
    </div>
  );
}
